import React, { useCallback, useEffect, useId, useState } from "react";
import { getReleaseMetadata, type ReleaseMetadata } from "@/lib/releaseMetadata";
import LogoWhiteStaging from "@/assets/images/kk-staging-logo.png";
import LogoWhiteProduction from "@/assets/images/kk-prod-logo.png";
import "./SidebarReleaseFlipCard.scss";

export type SidebarReleaseContext =
  | { kind: "super-admin" }
  | { kind: "tenant"; logoUrl?: string | null; displayName?: string | null };

export interface SidebarReleaseFlipCardProps {
  context?: SidebarReleaseContext;
  /** Inject metadata for tests; defaults to getReleaseMetadata(). */
  metadata?: ReleaseMetadata;
  collapsed?: boolean;
}

function krossKastLogoSrc(): string {
  switch (process.env.REACT_APP_ENV) {
    case "production":
      return LogoWhiteProduction;
    case "staging":
    default:
      return LogoWhiteStaging;
  }
}

function FrontFace({
  context,
  collapsed,
}: {
  context: SidebarReleaseContext;
  collapsed?: boolean;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const logoUrl = context.kind === "tenant" ? context.logoUrl : null;

  useEffect(() => {
    setImgFailed(false);
  }, [logoUrl]);

  if (context.kind === "tenant") {
    const name = (context.displayName || "Account").trim() || "Account";
    if (context.logoUrl && !imgFailed) {
      return (
        <img
          className="kk-release-flip-logo"
          src={context.logoUrl}
          alt=""
          onError={() => setImgFailed(true)}
        />
      );
    }
    return (
      <span className="kk-release-flip-name" title={name}>
        {collapsed ? name.slice(0, 1).toUpperCase() : name}
      </span>
    );
  }

  return (
    <img
      className="kk-release-flip-logo"
      src={krossKastLogoSrc()}
      alt="KrossKast"
    />
  );
}

function BackFace({ meta, collapsed }: { meta: ReleaseMetadata; collapsed?: boolean }) {
  const envLabel =
    meta.environment === "unknown"
      ? "LOCAL"
      : meta.environment.toUpperCase();

  if (collapsed) {
    return (
      <div className="kk-release-flip-meta kk-release-flip-meta--compact">
        <div className="kk-release-flip-line">{meta.gitSha}</div>
        <div className="kk-release-flip-line">{envLabel}</div>
      </div>
    );
  }

  return (
    <div className="kk-release-flip-meta">
      <div className="kk-release-flip-line">{meta.releaseId}</div>
      {meta.buildTimestamp ? (
        <div className="kk-release-flip-line">{meta.buildTimestamp}</div>
      ) : null}
      <div className="kk-release-flip-line">{envLabel}</div>
      <div className="kk-release-flip-line">{meta.gitSha}</div>
    </div>
  );
}

/**
 * Bottom-sidebar flip card: tenant/KrossKast logo ↔ deployed release metadata.
 * Application build metadata only — not a tenant software version.
 */
const SidebarReleaseFlipCard: React.FC<SidebarReleaseFlipCardProps> = ({
  context = { kind: "super-admin" },
  metadata,
  collapsed = false,
}) => {
  const [flipped, setFlipped] = useState(false);
  const meta = metadata ?? getReleaseMetadata();
  const labelId = useId();

  const toggle = useCallback(() => {
    setFlipped((v) => !v);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  const ariaLabel = flipped
    ? `Release ${meta.releaseId}, ${meta.environment}, commit ${meta.gitSha}. Activate to show logo.`
    : "Account logo. Activate to show release details.";

  return (
    <div className="kk-release-flip-slot" data-testid="sidebar-release-flip-card">
      <div
        className={`kk-release-flip${flipped ? " flipped" : ""}${collapsed ? " collapsed" : ""}`}
        role="button"
        tabIndex={0}
        aria-pressed={flipped}
        aria-labelledby={labelId}
        aria-label={ariaLabel}
        title={
          flipped
            ? "Click to show logo"
            : "Click to show release version"
        }
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span id={labelId} className="visually-hidden">
          {flipped ? "Release details" : "Account logo"}
        </span>
        <div className="kk-release-flip-inner">
          <div className="kk-release-flip-front" aria-hidden={flipped}>
            <FrontFace context={context} collapsed={collapsed} />
          </div>
          <div className="kk-release-flip-back" aria-hidden={!flipped}>
            <BackFace meta={meta} collapsed={collapsed} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidebarReleaseFlipCard;
